import { Button, Dropdown, FormInput } from '@basqet-dashboard/common/components';
import { FileUpload, InfoLabel, PepYesNoToggle, SidePanel } from '@components/onboarding';
import { useCompliance } from '@hooks/useCompliance';
import PropTypes from 'prop-types';
import React, { useState } from 'react';
import { toast } from 'react-toastify';

import { ID_FORMAT_RULES, KYB_SAMPLE_DOCS, validateIdNumber } from '../../../shared-constants';
import { ID_TYPE_OPTIONS } from '../../corporate-non-fi-constants';

const PepLabel = () => (
  <InfoLabel
    text="Is this a Politically Exposed Person (PEP)?"
    tooltip="Politically exposed persons are people who hold or have held public office, or are closely related to someone who does. Select Yes only if this applies."
  />
);

const UBOPanel = ({ entry, onSave, onClose, onIdUploaded }) => {
  const { uploadFile } = useCompliance();
  const [name, setName] = useState(entry?.name || '');
  const [isPep, setIsPep] = useState(entry?.is_pep || '');
  const [idType, setIdType] = useState(entry?.id_type || '');
  const [idNumber, setIdNumber] = useState(entry?.id_number || '');
  const [meansOfIdFile, setMeansOfIdFile] = useState(entry?.means_of_id_file || null);
  const [attachmentId, setAttachmentId] = useState(
    entry?.means_of_identification_attachment_id || null
  );
  const [errors, setErrors] = useState({});

  const validate = () => {
    const errs = {};
    if (!name.trim()) errs.name = 'Name is required';
    if (!isPep) errs.is_pep = 'Please select an option';
    if (!idType) errs.id_type = 'ID type is required';
    if (!idNumber.trim()) {
      errs.id_number = 'ID number is required';
    } else {
      const formatError = validateIdNumber(idType, idNumber);
      if (formatError) errs.id_number = formatError;
    }
    if (!meansOfIdFile && !attachmentId) errs.means_of_id_file = 'Means of ID document is required';
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const handleFileChange = async (file) => {
    if (!file) return;
    setMeansOfIdFile(file);
    try {
      const result = await uploadFile(file);
      setAttachmentId(result.attachment_id);
      toast.success('Means of ID of UBO uploaded successfully');
      onIdUploaded?.();
    } catch (error) {
      setMeansOfIdFile(null);
    }
  };

  const handleSave = () => {
    if (!validate()) return;
    onSave({
      name: name.trim(),
      is_pep: isPep,
      id_type: idType,
      id_number: idNumber.trim(),
      means_of_id_file: meansOfIdFile,
      means_of_identification_attachment_id: attachmentId
    });
  };

  return (
    <SidePanel title="Ultimate Beneficial Owner" cursiveTitle="Details" onClose={onClose}>
      <div className="side-panel__body">
        <div className="side-panel__fields">
          <FormInput
            label="Name of Ultimate Beneficial Owner"
            name="ubo_name"
            placeholder="First name, Last name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            error={errors.name}
          />
          <div>
            <PepLabel />
            <PepYesNoToggle value={isPep} onChange={setIsPep} />
            {errors.is_pep && <span className="corp-step-form__error">{errors.is_pep}</span>}
          </div>
          <div className="side-panel__row">
            <Dropdown
              name="ubo_id_type"
              formLabel="ID Type"
              placeholder="Select government ID"
              options={ID_TYPE_OPTIONS}
              register={{
                name: 'ubo_id_type',
                onChange: (e) => setIdType(e.target.value)
              }}
              defaultValue={idType}
              error={errors.id_type}
            />
            <FormInput
              label="ID Number"
              name="ubo_id_number"
              placeholder={ID_FORMAT_RULES[idType]?.placeholder || 'Enter ID number'}
              value={idNumber}
              onChange={(e) => setIdNumber(e.target.value)}
              error={errors.id_number}
            />
          </div>
          <div>
            <p className="side-panel__file-label">Means of ID of UBO</p>
            <p className="side-panel__file-description">
              Govt-issued ID for each Ultimate Beneficial Owner. All IDs must be valid.
            </p>
            <FileUpload
              value={meansOfIdFile}
              onChange={handleFileChange}
              onDelete={() => {
                setMeansOfIdFile(null);
                setAttachmentId(null);
              }}
              sampleTitle={{ normal: 'Sample', cursive: 'ID Document' }}
              sampleImage={KYB_SAMPLE_DOCS.MEANS_OF_ID_UBO}
              maxSizeMB={5}
              error={errors.means_of_id_file}
            />
          </div>
        </div>
      </div>
      <div className="side-panel__footer">
        <Button isFull onClick={handleSave}>
          Save and Continue
        </Button>
      </div>
    </SidePanel>
  );
};

UBOPanel.propTypes = {
  entry: PropTypes.object,
  onSave: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
  onIdUploaded: PropTypes.func
};

export { UBOPanel };
